import React, { useCallback, useEffect, useState } from 'react';
import assert from "assert";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Table,
  TableCaption,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import useLoginData from '../../../../../../hooks/useLoginData';
import Avatar, { avatarOptions } from '../../../../../SocialSidebar/Avatar';
import useCoveyAppState from '../../../../../../hooks/useCoveyAppState';
import { GuestData, LoginData } from '../../../../../../CoveyTypes';

interface LoginScreenProps {
  doLoginData: (logData: LoginData | GuestData) => Promise<void>
}

export default function LoginScreen({ doLoginData }: LoginScreenProps): JSX.Element {
  const loginData = useLoginData();
  const { apiClient } = useCoveyAppState();
  
  const {isOpen: isLoginModalOpen, onOpen: onLoginModalOpen, onClose: onLoginModalClose} = useDisclosure();
  const [loginModalUsername, setLoginModalUsername] = useState<string>();
  const [loginModalPassword, setLoginModalPassword] = useState<string>();
  const openLoginModal = useCallback(()=>{
    setLoginModalUsername("");
    setLoginModalPassword("");
    onLoginModalOpen();
  }, [onLoginModalOpen]);
  const closeLoginModal = useCallback(()=>{
    onLoginModalClose();
  }, [onLoginModalClose]);
  const [createAccountModalUsername, setCreateAccountModalUsername] = useState<string>();
  const [createAccountModalPassword, setCreateAccountModalPassword] = useState<string>();
  const [createAccountModalNickname, setCreateAccountModalNickname] = useState<string>();
  const {isOpen: isCreateAccountModalOpen, onOpen: onCreateAccountModalOpen, onClose: onCreateAccountModalClose} = useDisclosure();
  const openCreateAccountModal = useCallback(()=>{
    setCreateAccountModalUsername("");
    setCreateAccountModalPassword("");
    setCreateAccountModalNickname("");
    onCreateAccountModalOpen();
  }, [onCreateAccountModalOpen]);
  const closeCreateAccountModal = useCallback(()=>{
    onCreateAccountModalClose();
  }, [onCreateAccountModalClose]);
  const {isOpen: isLogoutModalOpen, onOpen: onLogoutModalOpen, onClose: onLogoutModalClose} = useDisclosure();
  const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);
  const [deletingAccountModalPassword, setDeletingAccountModalPassword] = useState<string>();
  const openLogoutModal = useCallback(()=>{
    setIsDeletingAccount(false);
    setDeletingAccountModalPassword("");
    onLogoutModalOpen();
  }, [onLogoutModalOpen]);
  const closeLogoutModal = useCallback(()=>{
    onLogoutModalClose();
  }, [onLogoutModalClose]);
  const {isOpen: isUserDetailsModalOpen, onOpen: onUserDetailsModalOpen, onClose: onUserDetailsModalClose} = useDisclosure();
  const [userDetailsModalNickname, setUserDetailsModalNickname] = useState<string>(loginData.nickname);
  // Nest the state in the check for if loginData.loggedIn is true
  const [userDetailsModalAvatar, setUserDetailsModalAvatar] = useState<number>(loginData.loggedIn ? loginData.avatar : 0);
  const openUserDetailsModal = useCallback(()=>{
    setUserDetailsModalNickname("");
    onUserDetailsModalOpen();
  }, [onUserDetailsModalOpen]);
  const closeUserDetailsModal = useCallback(()=>{
    onUserDetailsModalClose();
  }, [onUserDetailsModalClose]);

  const toast = useToast();
  const processUpdates = async (action: string) =>{
    if(action === 'login'){
      try{
        if (loginModalUsername !== undefined && loginModalPassword !== undefined) {
          const loginResponse = await apiClient.accountLogin({
            accountUsername: loginModalUsername,
            accountPassword: loginModalPassword
          });
          const avatarResponse = await apiClient.accountGetAvatar({
            accountToken: loginResponse.accountToken
          });
          const nicknameResponse = await apiClient.accountGetNickname({
            accountToken: loginResponse.accountToken
          });
          setUserDetailsModalAvatar(avatarResponse.accountAvatar);
          await doLoginData({
            username: loginModalUsername,
            auth_token: loginResponse.accountToken,
            avatar: avatarResponse.accountAvatar,
            nickname: nicknameResponse.accountNickname,
            loggedIn: true 
          });
        } else {
          toast({
            title: 'Unable to login to account',
            description: 'Please enter valid account credentials',
            status: 'error',
          })
          return;
        }

        toast({
          title: 'User Logged In',
          status: 'success'
        })
        closeLoginModal();
      }catch(err){
        toast({
          title: 'Unable to login',
          description: String(err),
          status: 'error'
        });
      }
    } else if (action === 'createAccount') {
      try{
        if (createAccountModalUsername !== undefined && createAccountModalNickname !== undefined && createAccountModalPassword !== undefined) {
          const newAccount = await apiClient.accountCreate({
            accountUsername: createAccountModalUsername,
            accountNickname: createAccountModalNickname,
            accountPassword: createAccountModalPassword
          });
          const loginResponse = await apiClient.accountLogin({
            accountUsername: newAccount.accountUsername,
            accountPassword: newAccount.accountPassword
          });
          const avatarResponse = await apiClient.accountGetAvatar({
            accountToken: loginResponse.accountToken
          });
          const nicknameResponse = await apiClient.accountGetNickname({
            accountToken: loginResponse.accountToken
          });
          setUserDetailsModalAvatar(avatarResponse.accountAvatar);
          await doLoginData({
            username: createAccountModalUsername,
            auth_token: loginResponse.accountToken,
            avatar: avatarResponse.accountAvatar,
            nickname: nicknameResponse.accountNickname,
            loggedIn: true 
          });
        } else {
          toast({
            title: 'Unable to create account',
            description: 'Please enter valid account credentials',
            status: 'error',
          })
          return;
        }
        toast({
          title: 'User Created Account',
          status: 'success'
        })
        closeCreateAccountModal();
      }catch(err){
        toast({
          title: 'Unable to create account',
          description: String(err),
          status: 'error'
        });
      }
    } else if (action === 'logout') {
      if (isDeletingAccount) {
        try{
          //  TODO: Add await apiClient.apiCall();
          let token = "";
          if (loginData.loggedIn)
          {
            token = loginData.auth_token;
          }
          if (deletingAccountModalPassword !== undefined && token !== "") {
            const deleteResponse = await apiClient.accountDelete({
              accountToken: token,
              accountPassword: deletingAccountModalPassword
            });
            await doLoginData({nickname: "", loggedIn: false});
          } else {
            toast({
              title: 'Unable to delete account',
              description: 'Please enter a valid password',
              status: 'error',
            })
            return;
          }
          await doLoginData({nickname: "", loggedIn: false});
          toast({
            title: 'User Deleted Account',
            status: 'success'
          })
          closeLogoutModal();
        }catch(err){
          toast({
            title: 'Unable to Delete Account',
            description: String(err),
            status: 'error'
          });
        }
      } else {
        try{
          //  TODO: Add await apiClient.apiCall(); - this should actually just clear the token and set the state to guest
          await doLoginData({nickname: "", loggedIn: false});
          toast({
            title: 'User Logged Out',
            status: 'success'
          })
          closeLogoutModal();
        }catch(err){
          toast({
            title: 'Unable to logout',
            description: String(err),
            status: 'error'
          });
        }
      }
    } else if (action === 'editDetails') {
      try{
        //  TODO: Add await apiClient.apiCall();
        if (loginData.loggedIn) {
          if (loginData.avatar !== userDetailsModalAvatar && loginData.nickname !== userDetailsModalNickname && userDetailsModalNickname !== "") {
            try {
              const newAvatar = await apiClient.accountUpdateAvatar({
                              accountToken: loginData.auth_token,
                              accountAvatar: userDetailsModalAvatar
              }); 
              setUserDetailsModalAvatar(newAvatar.accountAvatar);
              const newNickname = await apiClient.accountUpdateNickname({
                accountToken: loginData.auth_token,
                accountNickname: userDetailsModalNickname
              });  
              await doLoginData({
                username: loginData.username,
                auth_token: loginData.auth_token,
                avatar: newAvatar.accountAvatar,
                nickname: newNickname.accountNickname,
                loggedIn: true 
              });  
            }catch(err){
              toast({
                title: 'Unable to edit user details',
                description: String(err),
                status: 'error'
              });
            }
          } else {
            try {
              if (loginData.avatar !== userDetailsModalAvatar) {
                const newAvatar = await apiClient.accountUpdateAvatar({
                  accountToken: loginData.auth_token,
                  accountAvatar: userDetailsModalAvatar
                });
                setUserDetailsModalAvatar(newAvatar.accountAvatar);
                await doLoginData({
                  username: loginData.username,
                  auth_token: loginData.auth_token,
                  avatar: newAvatar.accountAvatar,
                  nickname: loginData.nickname,
                  loggedIn: true 
                });
              }
            } catch(err){
              toast({
                title: 'Unable to edit avatar',
                description: String(err),
                status: 'error'
              });
            }
            try {
              if (loginData.nickname !== userDetailsModalNickname && userDetailsModalNickname !== "") {
                const newNickname = await apiClient.accountUpdateNickname({
                  accountToken: loginData.auth_token,
                  accountNickname: userDetailsModalNickname
                });
                await doLoginData({
                  username: loginData.username,
                  auth_token: loginData.auth_token,
                  avatar: loginData.avatar,
                  nickname: newNickname.accountNickname,
                  loggedIn: true 
                });
              }
            } catch(err){
              toast({
                title: 'Unable to edit details',
                description: String(err),
                status: 'error'
              });
            }
          }
        } else {
          toast({
            title: 'Unable to edit account',
            description: 'Must be logged in to edit details',
            status: 'error',
          })
          return;
        }
        toast({
          title: 'User Edited Details',
          status: 'success'
        })
        closeUserDetailsModal();
      }catch(err){
        toast({
          title: 'Unable to edit details',
          description: String(err),
          status: 'error'
        });
      }
    }
  };

  // TODO: Add avatar editing functionality

  if (loginData.loggedIn) {
    return (
      <>
        <form>
          <Stack>
            <Box p="4" borderWidth="1px" borderRadius="lg">
              <Heading as="h2" size="lg" marginBottom="5">Manage Your Account</Heading>
              <Box>
                <HStack>
                  <Button data-testid="openUserDetailsModalButton" onClick={openUserDetailsModal}>Edit User Details</Button>
                  <Button data-testid="openLogoutModalButton" onClick={openLogoutModal}>Log Out/Delete Account</Button>
                </HStack>
              </Box>
              <Modal isOpen={isUserDetailsModalOpen} onClose={closeUserDetailsModal}>
                <ModalOverlay/>
                <ModalContent>
                  <ModalHeader>Edit Details: {loginData.username}</ModalHeader>
                  <ModalCloseButton/>
                  <form onSubmit={(ev)=>{ev.preventDefault(); processUpdates('editDetails')}}>
                    <ModalBody pb={6}>
                      <FormControl isRequired>
                        <FormLabel htmlFor='nickname'>Default Nickname</FormLabel>
                        <Input id='nickname' placeholder={loginData.nickname} name="nickname" value={userDetailsModalNickname} onChange={(ev)=>setUserDetailsModalNickname(ev.target.value)} />
                      </FormControl>
                      <FormControl isRequired isInvalid={userDetailsModalAvatar < 0 || userDetailsModalAvatar >= avatarOptions.length }>
                        <FormLabel htmlFor='avatar'>Avatar: <Avatar avatar={userDetailsModalAvatar} /></FormLabel>
                        <NumberInput id='avatar' name="avatar" min={1} max={avatarOptions.length} value={userDetailsModalAvatar + 1} onChange={(str, num)=>setUserDetailsModalAvatar(num - 1)}>
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        {(userDetailsModalAvatar < 0 || userDetailsModalAvatar >= avatarOptions.length) ? (
                          <FormErrorMessage>Invalid Avatar Option</FormErrorMessage>
                        ):<></>}
                      </FormControl>
                    </ModalBody>

                    <ModalFooter>
                      <Button data-testid='editDetailsButton' colorScheme="blue" mr={3} value="update" name='action2' onClick={()=>processUpdates('editDetails')}>
                        Update
                      </Button>
                      <Button onClick={closeUserDetailsModal}>Cancel</Button>
                    </ModalFooter>
                  </form>
                </ModalContent>
              </Modal>
              <Modal isOpen={isLogoutModalOpen} onClose={closeLogoutModal}>
                <ModalOverlay/>
                <ModalContent>
                  <ModalHeader>Logout: {loginData.username}</ModalHeader>
                  <ModalCloseButton/>
                  <form onSubmit={(ev)=>{ev.preventDefault(); processUpdates('logout')}}>
                    <ModalBody>
                      <FormControl mt={4}>
                        <FormLabel htmlFor='isDeletingAccount'>Delete Account</FormLabel>
                        <Checkbox id="isDeletingAccount" name="isDeletingAccount"  isChecked={isDeletingAccount} onChange={(e)=>setIsDeletingAccount(e.target.checked)} />
                      </FormControl>
                      
                      {isDeletingAccount ? (
                        <FormControl isRequired isInvalid={deletingAccountModalPassword===""}>
                          <FormLabel htmlFor='password'>Confirm Password</FormLabel>
                          <Input id='password' name="password" value={deletingAccountModalPassword} onChange={(ev)=>setDeletingAccountModalPassword(ev.target.value)} />
                          {(deletingAccountModalPassword==="") ? (
                            <FormErrorMessage>Password Cannot Be Empty</FormErrorMessage>
                          ):<></>}
                        </FormControl>
                        ) : (<></>)}
                    </ModalBody>
                    <ModalFooter>
                      <Button data-testid='logoutButton' colorScheme={isDeletingAccount ? "red" : "blue"} mr={3} value="logout" name='action2' onClick={()=>processUpdates('logout')}>
                        {isDeletingAccount ? "Delete Account" : "Logout"}
                      </Button>
                      <Button onClick={closeLogoutModal}>Cancel</Button>
                    </ModalFooter>
                  </form>
                </ModalContent>
              </Modal>
            </Box>
          </Stack>
        </form>
      </>
    );
  } 
  return (
    <>
      <form>
        <Stack>
          <Box p="4" borderWidth="1px" borderRadius="lg">
            <Heading as="h2" size="lg" marginBottom="5">Login or Create Account</Heading>
            <Box>
              <HStack>
                <Button data-testid="openLoginModalButton" onClick={openLoginModal}>Login</Button>
                <Button data-testid="openCreateAccountModalButton" onClick={openCreateAccountModal}>Create Account</Button>
              </HStack>
            </Box>
            <Modal isOpen={isLoginModalOpen} onClose={closeLoginModal}>
              <ModalOverlay/>
              <ModalContent>
                <ModalHeader>Login</ModalHeader>
                <ModalCloseButton/>
                <form onSubmit={(ev)=>{ev.preventDefault(); processUpdates('login')}}>
                  <ModalBody pb={6}>
                    <FormControl isRequired isInvalid={loginModalUsername===""}>
                      <FormLabel htmlFor='username'>Username</FormLabel>
                      <Input id='username' name="username" value={loginModalUsername} onChange={(ev)=>setLoginModalUsername(ev.target.value)} />
                      {(loginModalUsername==="") ? (
                        <FormErrorMessage>Username Cannot Be Empty</FormErrorMessage>
                      ):<></>}
                    </FormControl>
                    <FormControl isRequired isInvalid={loginModalPassword===""}>
                      <FormLabel htmlFor='password'>Password</FormLabel>
                      <Input id='password' name="password" value={loginModalPassword} onChange={(ev)=>setLoginModalPassword(ev.target.value)} />
                      {(loginModalPassword==="") ? (
                        <FormErrorMessage>Password Cannot Be Empty</FormErrorMessage>
                      ):<></>}
                    </FormControl>
                  </ModalBody>
                  <ModalFooter>
                    <Button data-testid='loginButton' colorScheme="blue" mr={3} value="login" name='action2' onClick={()=>processUpdates('login')}>
                      Login
                    </Button>
                    <Button onClick={closeLoginModal}>Cancel</Button>
                  </ModalFooter>
                </form>
              </ModalContent>
            </Modal>
            <Modal isOpen={isCreateAccountModalOpen} onClose={closeCreateAccountModal}>
              <ModalOverlay/>
              <ModalContent>
                <ModalHeader>Create Account</ModalHeader>
                <ModalCloseButton/>
                <form onSubmit={(ev)=>{ev.preventDefault(); processUpdates('createAccount')}}>
                  <ModalBody pb={6}>
                    <FormControl isRequired isInvalid={createAccountModalUsername===""}>
                      <FormLabel htmlFor='username'>Username</FormLabel>
                      <Input id='username' name="username" value={createAccountModalUsername} onChange={(ev)=>setCreateAccountModalUsername(ev.target.value)} />
                      {(createAccountModalUsername==="") ? (
                        <FormErrorMessage>Username Cannot Be Empty</FormErrorMessage>
                      ):<></>}
                    </FormControl>
                    <FormControl isRequired isInvalid={createAccountModalPassword===""}>
                      <FormLabel htmlFor='password'>Password</FormLabel>
                      <Input id='password' name="password" value={createAccountModalPassword} onChange={(ev)=>setCreateAccountModalPassword(ev.target.value)} />
                      {(createAccountModalPassword==="") ? (
                        <FormErrorMessage>Password Cannot Be Empty</FormErrorMessage>
                      ):<></>}
                    </FormControl>
                    <FormControl isRequired isInvalid={createAccountModalNickname===""}>
                      <FormLabel htmlFor='nickname'>Nickname</FormLabel>
                      <Input id='nickname' name="nickname" value={createAccountModalNickname} onChange={(ev)=>setCreateAccountModalNickname(ev.target.value)} />
                      {(createAccountModalNickname==="") ? (
                        <FormErrorMessage>Nickname Cannot Be Empty</FormErrorMessage>
                      ):<></>}
                    </FormControl>
                  </ModalBody>
                  <ModalFooter>
                    <Button data-testid='createAccountButton' colorScheme="blue" mr={3} value="createAccount" name='action2' onClick={()=>processUpdates('createAccount')}>
                      Create Account
                    </Button>
                    <Button onClick={closeCreateAccountModal}>Cancel</Button>
                  </ModalFooter>
                </form>
              </ModalContent>
            </Modal>
          </Box>
        </Stack>
      </form>
    </>
  );
  
}